/**
 * 🏗️ MILESTONE DATA PROVIDER
 * 
 * Single source of truth for milestone data across all components.
 * Replaces individual component queries with unified data access.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useFirestore } from './FirestoreContext';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const MilestoneDataContext = createContext();

export const useMilestones = (options = {}) => {
  const context = useContext(MilestoneDataContext);
  if (!context) {
    throw new Error('useMilestones must be used within a MilestoneDataProvider');
  }
  
  const { 
    incidentId, 
    status, 
    includeDeleted = false,
    enableRealTime = true 
  } = options;
  
  const { getMilestones, loading, error, ensureIncidentListener } = context;
  
  // 🎯 AUTOMATICALLY ENSURE LISTENER IS ACTIVE
  useEffect(() => {
    if (incidentId && enableRealTime) {
      ensureIncidentListener(incidentId);
    }
  }, [incidentId, enableRealTime, ensureIncidentListener]);
  
  // Get milestones for specific incident with optional filters
  const milestones = getMilestones(incidentId, { status, includeDeleted });
  
  return { 
    milestones, 
    loading: loading[incidentId] || false, 
    error: error[incidentId] || null,
    totalCount: milestones.length
  };
};

export const MilestoneDataProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  
  // State management
  const [milestoneData, setMilestoneData] = useState({}); // { incidentId: milestone[] }
  const [loading, setLoading] = useState({}); // { incidentId: boolean }
  const [error, setError] = useState({}); // { incidentId: error }
  
  // Listener management
  const unsubscribers = useRef({}); // { incidentId: unsubscribe }
  const activeIncidents = useRef(new Set()); // Track which incidents are being monitored
  
  // 🔍 DEBUG: Add comprehensive logging
  const logMilestoneUpdate = useCallback((incidentId, milestones, source) => {
    console.log(`📊 Milestone data updated for incident ${incidentId}:`, {
      count: milestones.length,
      source,
      milestones: milestones.map(m => ({
        id: m.id,
        title: m.title,
        status: m.status,
        deleted: m.deleted,
        dueDate: m.dueDate?.toDate?.() || m.dueDate
      }))
    });
  }, []);
  
  /**
   * 🎯 CANONICAL MILESTONE QUERY
   * This is the single source of truth for how milestones should be queried
   */
  const createMilestoneQuery = useCallback((incidentId, userOrgId, includeDeleted = false) => {
    if (!incidentId || !userOrgId) {
      throw new Error('createMilestoneQuery requires incidentId and userOrgId');
    }
    
    console.log(`🔍 Creating canonical milestone query:`, {
      incidentId,
      userOrgId,
      includeDeleted,
      collection: 'c3_milestones'
    });
    
    let milestoneQuery = query(
      collection(db, 'c3_milestones'),
      where('incidentId', '==', incidentId),
      where('orgId', '==', userOrgId)
    );
    
    // Always filter deleted unless explicitly requested
    if (!includeDeleted) {
      milestoneQuery = query(milestoneQuery, where('deleted', '==', false));
    }
    
    // Consistent ordering: dueDate first, then createdAt for items without dueDate
    milestoneQuery = query(
      milestoneQuery,
      orderBy('dueDate', 'asc'),
      orderBy('createdAt', 'asc')
    );
    
    return milestoneQuery;
  }, [db]);
  
  /**
   * 🎧 Set up real-time listener for an incident
   */
  const setupIncidentListener = useCallback(async (incidentId) => {
    if (!currentUser?.orgId || !incidentId) {
      console.warn('⚠️ Cannot setup milestone listener: missing auth or incidentId');
      return;
    }
    
    // Prevent duplicate listeners
    if (unsubscribers.current[incidentId]) {
      console.log(`🔄 Milestone listener already exists for incident ${incidentId}`);
      return;
    }
    
    try {
      setLoading(prev => ({ ...prev, [incidentId]: true }));
      setError(prev => ({ ...prev, [incidentId]: null }));
      
      console.log(`🎧 Setting up milestone listener for incident ${incidentId}`);
      
      const milestoneQuery = createMilestoneQuery(incidentId, currentUser.orgId, false);
      
      const unsubscribe = onSnapshot(
        milestoneQuery,
        (snapshot) => {
          const milestones = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setMilestoneData(prev => ({
            ...prev,
            [incidentId]: milestones
          }));
          
          setLoading(prev => ({ ...prev, [incidentId]: false }));
          
          logMilestoneUpdate(incidentId, milestones, 'real-time listener');
          
          // Track document changes for debugging
          const changes = snapshot.docChanges();
          if (changes.length > 0) {
            console.log(`📡 Milestone changes for incident ${incidentId}:`, {
              added: changes.filter(c => c.type === 'added').length,
              modified: changes.filter(c => c.type === 'modified').length,
              removed: changes.filter(c => c.type === 'removed').length
            });
          }
        },
        (error) => {
          console.error(`❌ Milestone listener error for incident ${incidentId}:`, error);
          setError(prev => ({ ...prev, [incidentId]: error }));
          setLoading(prev => ({ ...prev, [incidentId]: false }));
        }
      );
      
      unsubscribers.current[incidentId] = unsubscribe;
      activeIncidents.current.add(incidentId);
      
      // Milestone listener active
      
    } catch (error) {
      console.error(`❌ Failed to setup milestone listener for incident ${incidentId}:`, error);
      setError(prev => ({ ...prev, [incidentId]: error }));
      setLoading(prev => ({ ...prev, [incidentId]: false }));
    }
  }, [currentUser?.orgId, createMilestoneQuery, logMilestoneUpdate]);
  
  /**
   * 🛑 Clean up listener for an incident
   */
  const cleanupIncidentListener = useCallback((incidentId) => {
    const unsubscribe = unsubscribers.current[incidentId];
    if (unsubscribe) {
      console.log(`🛑 Cleaning up milestone listener for incident ${incidentId}`);
      unsubscribe();
      delete unsubscribers.current[incidentId];
      activeIncidents.current.delete(incidentId);
      
      // Clean up data
      setMilestoneData(prev => {
        const { [incidentId]: removed, ...rest } = prev;
        return rest;
      });
      setLoading(prev => {
        const { [incidentId]: removed, ...rest } = prev;
        return rest;
      });
      setError(prev => {
        const { [incidentId]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, []);
  
  /**
   * 📊 Get milestones for an incident with optional filtering
   */
  const getMilestones = useCallback((incidentId, options = {}) => {
    if (!incidentId) return [];
    
    const { status, includeDeleted = false } = options;
    let milestones = milestoneData[incidentId] || [];
    
    // Apply additional client-side filters if needed
    if (status) {
      milestones = milestones.filter(milestone => milestone.status === status);
    }
    
    if (!includeDeleted) {
      milestones = milestones.filter(milestone => milestone.deleted !== true);
    }
    
    return milestones;
  }, [milestoneData]);
  
  /**
   * 🔄 Force refresh milestone data for an incident
   */
  const refreshMilestones = useCallback((incidentId) => {
    if (!incidentId) return;
    
    console.log(`🔄 Force refreshing milestones for incident ${incidentId}`);
    
    // Clean up existing listener
    cleanupIncidentListener(incidentId);
    
    // Set up new listener
    setupIncidentListener(incidentId);
  }, [cleanupIncidentListener, setupIncidentListener]);
  
  /**
   * 🎯 Ensure milestone listener is active for an incident
   */
  const ensureIncidentListener = useCallback((incidentId) => {
    if (!incidentId) return;
    
    if (!activeIncidents.current.has(incidentId)) {
      setupIncidentListener(incidentId);
    }
  }, [setupIncidentListener]);
  
  // Cleanup all listeners on unmount
  useEffect(() => {
    return () => {
      console.log('🛑 Cleaning up all milestone listeners');
      Object.values(unsubscribers.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
      unsubscribers.current = {};
      activeIncidents.current.clear();
    };
  }, []);
  
  // 🚨 RED FLAG: Detect rogue milestone listeners
  useEffect(() => {
    const checkForRogueListeners = () => {
      const activeListenerCount = Object.keys(unsubscribers.current).length;
      if (activeListenerCount > 5) {
        console.warn(`🚨 RED FLAG: ${activeListenerCount} milestone listeners active. Potential memory leak!`, {
          activeIncidents: Array.from(activeIncidents.current),
          listenerCount: activeListenerCount
        });
      }
    };
    
    const interval = setInterval(checkForRogueListeners, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);
  
  const contextValue = {
    milestoneData,
    loading,
    error,
    getMilestones,
    refreshMilestones,
    ensureIncidentListener,
    cleanupIncidentListener,
    
    // Debug utilities
    getActiveIncidents: () => Array.from(activeIncidents.current),
    getListenerCount: () => Object.keys(unsubscribers.current).length
  };
  
  return (
    <MilestoneDataContext.Provider value={contextValue}>
      {children}
    </MilestoneDataContext.Provider>
  );
};

// Export context for direct access if needed
export { MilestoneDataContext };

// Export hook for milestone operations using server functions
export const useMilestoneOperations = () => {
  const { db } = useFirestore();
  
  return {
    /**
     * Create a new milestone using server function
     */
    createMilestone: async (milestoneData) => {
      try {
        console.log('🛠️ Creating milestone via server function:', milestoneData);
        
        const createMilestone = httpsCallable(getFunctions(undefined, 'australia-southeast2'), 'upsertMilestone');
        const result = await createMilestone({
          milestoneData
        });
        
        // Milestone created
        return result.data;
        
      } catch (error) {
        console.error('❌ Failed to create milestone:', error);
        throw error;
      }
    },
    
    /**
     * Update an existing milestone using server function
     */
    updateMilestone: async (milestoneId, milestoneData) => {
      try {
        console.log('🛠️ Updating milestone via server function:', { milestoneId, milestoneData });
        
        const updateMilestone = httpsCallable(getFunctions(undefined, 'australia-southeast2'), 'upsertMilestone');
        const result = await updateMilestone({
          milestoneId,
          milestoneData
        });
        
        // Milestone updated
        return result.data;
        
      } catch (error) {
        console.error('❌ Failed to update milestone:', error);
        throw error;
      }
    },
    
    /**
     * Delete a milestone using server function
     */
    deleteMilestone: async (milestoneId) => {
      try {
        console.log('🛠️ Deleting milestone via server function:', milestoneId);
        
        const deleteMilestone = httpsCallable(getFunctions(undefined, 'australia-southeast2'), 'deleteMilestone');
        const result = await deleteMilestone({
          milestoneId
        });
        
        // Milestone deleted
        return result.data;
        
      } catch (error) {
        console.error('❌ Failed to delete milestone:', error);
        throw error;
      }
    }
  };
};
